<template>
  <div>
    <!-- ============================================ -->
    <!-- SECTION 1: REPORT (Metrics 1-6)              -->
    <!-- API: /marg/retailer-orders-report            -->
    <!-- Variable: report                              -->
    <!-- ============================================ -->
    <b-card no-body>
      <b-card-body>
        <b-card-title class="d-flex justify-content-between align-items-center mb-2">
          <span>Order Activity</span>
        </b-card-title>

        <b-alert
          :show="reportError ? true : false"
          variant="danger"
          dismissible
          @dismissed="reportError = ''"
        >
          <div class="alert-body">
            {{ reportError }}
          </div>
        </b-alert>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="4"
            lg="3"
          >
            <b-form-group label="Date Range">
              <flat-picker
                v-model="reportDateRange"
                placeholder="Select Date Range"
                :config="{ mode: 'range' }"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            lg="3"
            class="d-flex align-items-end"
          >
            <b-button
              variant="primary"
              class="mb-1"
              :disabled="reportLoading"
              @click="fetchReport"
            >
              <feather-icon icon="RefreshCwIcon" />
              Refresh
              <b-spinner
                v-if="reportLoading"
                small
                label="Small Spinner"
              />
            </b-button>
          </b-col>
        </b-row>

        <div
          v-if="report.date_range"
          class="text-muted small mb-2"
        >
          Comparing
          <strong>{{ report.date_range.start_date }} to {{ report.date_range.end_date }}</strong>
          vs prior period
          <strong v-if="report.prior_date_range">
            {{ report.prior_date_range.start_date }} to {{ report.prior_date_range.end_date }}
          </strong>
        </div>

        <div
          v-if="reportLoading && !hasReportData"
          class="text-center py-3"
        >
          <b-spinner
            label="Loading"
            variant="primary"
          />
        </div>

        <b-row v-if="hasReportData">
          <!-- 1. Total No of Buyers -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div
              id="metric-total-buyers"
              class="metric-card metric-card-primary"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Total No of Buyers
                </div>
                <div class="metric-value">
                  {{ formatNumber(report.all_retailers && report.all_retailers.count) }}
                </div>
              </div>
              <b-popover
                target="metric-total-buyers"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  Total Buyers ({{ (report.all_retailers && report.all_retailers.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!report.all_retailers || !report.all_retailers.retailers || report.all_retailers.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in report.all_retailers.retailers"
                      :key="`all-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.user_full_name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                      <div
                        v-if="item.total_order_value !== undefined"
                        class="text-primary small"
                      >
                        {{ formatCurrency(item.total_order_value) }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>

          <!-- 2. New Buyers -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div
              id="metric-new-buyers"
              class="metric-card metric-card-success"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="UserPlusIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  New Buyers
                </div>
                <div class="metric-value">
                  {{ formatNumber(report.first_time_retailers && report.first_time_retailers.count) }}
                </div>
              </div>
              <b-popover
                target="metric-new-buyers"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  New Buyers ({{ (report.first_time_retailers && report.first_time_retailers.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!report.first_time_retailers || !report.first_time_retailers.retailers || report.first_time_retailers.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in report.first_time_retailers.retailers"
                      :key="`new-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.user_full_name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                      <div
                        v-if="item.total_order_value !== undefined"
                        class="text-primary small"
                      >
                        {{ formatCurrency(item.total_order_value) }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>

          <!-- 3. Repeating Buyers -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div
              id="metric-repeating-buyers"
              class="metric-card metric-card-info"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="RepeatIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Repeating Buyers
                </div>
                <div class="metric-value">
                  {{ formatNumber(report.returning_retailers && report.returning_retailers.count) }}
                </div>
              </div>
              <b-popover
                target="metric-repeating-buyers"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  Repeating Buyers ({{ (report.returning_retailers && report.returning_retailers.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!report.returning_retailers || !report.returning_retailers.retailers || report.returning_retailers.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in report.returning_retailers.retailers"
                      :key="`rep-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.user_full_name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                      <div
                        v-if="item.total_order_value !== undefined"
                        class="text-primary small"
                      >
                        {{ formatCurrency(item.total_order_value) }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>

          <!-- 4. Total Order Value -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div class="metric-card metric-card-warning">
              <div class="metric-icon">
                <feather-icon
                  icon="ShoppingCartIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Total Order Value
                </div>
                <div class="metric-value">
                  {{ formatCurrency(report.total_order_value && report.total_order_value.current) }}
                </div>
                <div
                  v-if="report.total_order_value && report.total_order_value.percentage_change !== null && report.total_order_value.percentage_change !== undefined"
                  class="metric-change"
                  :class="changeClass(report.total_order_value.percentage_change)"
                >
                  <feather-icon
                    :icon="report.total_order_value.percentage_change >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                    size="14"
                  />
                  {{ formatPercent(report.total_order_value.percentage_change) }} from last period
                </div>
              </div>
            </div>
          </b-col>

          <!-- 5. Total Billing Value -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div class="metric-card metric-card-secondary">
              <div class="metric-icon">
                <feather-icon
                  icon="FileTextIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Total Billing Value
                </div>
                <div class="metric-value">
                  {{ formatCurrency(report.total_billing_value && report.total_billing_value.current) }}
                </div>
                <div
                  v-if="report.total_billing_value && report.total_billing_value.percentage_change !== null && report.total_billing_value.percentage_change !== undefined"
                  class="metric-change"
                  :class="changeClass(report.total_billing_value.percentage_change)"
                >
                  <feather-icon
                    :icon="report.total_billing_value.percentage_change >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                    size="14"
                  />
                  {{ formatPercent(report.total_billing_value.percentage_change) }} from last period
                </div>
              </div>
            </div>
          </b-col>

          <!-- 6. Total Orders -->
          <b-col
            cols="12"
            sm="6"
            lg="4"
            class="mb-2"
          >
            <div class="metric-card metric-card-primary">
              <div class="metric-icon">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Total Orders
                </div>
                <div class="metric-value">
                  {{ formatNumber(report.total_order_count && report.total_order_count.current) }}
                </div>
                <div
                  v-if="report.total_order_count && report.total_order_count.percentage_change !== null && report.total_order_count.percentage_change !== undefined"
                  class="metric-change"
                  :class="changeClass(report.total_order_count.percentage_change)"
                >
                  <feather-icon
                    :icon="report.total_order_count.percentage_change >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                    size="14"
                  />
                  {{ formatPercent(report.total_order_count.percentage_change) }} from last period
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <div
          v-if="!reportLoading && !hasReportData && !reportError"
          class="text-center py-4 text-muted"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p class="mt-1">
            Select a date range to view order activity
          </p>
        </div>
      </b-card-body>
    </b-card>

    <!-- ============================================ -->
    <!-- SECTION 2: ACTIVITY (Metrics 7-8)            -->
    <!-- API: /marg/retailers/order-activity          -->
    <!-- Variable: activity                            -->
    <!-- ============================================ -->
    <b-card no-body>
      <b-card-body>
        <b-card-title class="d-flex justify-content-between align-items-center mb-2">
          <span>Retailer Status Report</span>
        </b-card-title>

        <b-alert
          :show="activityError ? true : false"
          variant="danger"
          dismissible
          @dismissed="activityError = ''"
        >
          <div class="alert-body">
            {{ activityError }}
          </div>
        </b-alert>

        <b-row class="mb-2">
          <b-col
            cols="12"
            md="4"
            lg="3"
          >
            <b-form-group label="Date Range">
              <flat-picker
                v-model="activityDateRange"
                placeholder="Select Date Range"
                :config="{ mode: 'range' }"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            lg="3"
          >
            <b-form-group label="Active Users Only">
              <custom-v-select
                id="active-user"
                v-model="activeUser"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="activeUserOptions"
                label="text"
                placeholder="Select"
                :reduce="option => option.value"
                @input="fetchActivity"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
            lg="3"
            class="d-flex align-items-end"
          >
            <b-button
              variant="primary"
              class="mb-1"
              :disabled="activityLoading"
              @click="fetchActivity"
            >
              <feather-icon icon="RefreshCwIcon" />
              Refresh
              <b-spinner
                v-if="activityLoading"
                small
                label="Small Spinner"
              />
            </b-button>
          </b-col>
        </b-row>

        <div
          v-if="activityLoading && !hasActivityData"
          class="text-center py-3"
        >
          <b-spinner
            label="Loading"
            variant="primary"
          />
        </div>

        <b-row v-if="hasActivityData">
          <!-- Ordered in Window -->
          <b-col
            cols="12"
            sm="6"
            lg="3"
            class="mb-2"
          >
            <div
              id="metric-ordered-window"
              class="metric-card metric-card-success"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Ordered in Window
                </div>
                <div class="metric-value">
                  {{ formatNumber(activity.ordered_in_window && activity.ordered_in_window.count) }}
                </div>
                <div class="metric-sublabel">
                  Active retailers in selected period
                </div>
              </div>
              <b-popover
                target="metric-ordered-window"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  Ordered in Window ({{ (activity.ordered_in_window && activity.ordered_in_window.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!activity.ordered_in_window || !activity.ordered_in_window.retailers || activity.ordered_in_window.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in activity.ordered_in_window.retailers"
                      :key="`ow-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>

          <!-- 7. No Orders in Window -->
          <b-col
            cols="12"
            sm="6"
            lg="3"
            class="mb-2"
          >
            <div
              id="metric-no-orders-window"
              class="metric-card metric-card-danger"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  No Orders in Window
                </div>
                <div class="metric-value">
                  {{ formatNumber(activity.no_orders_in_window && activity.no_orders_in_window.count) }}
                </div>
                <div class="metric-sublabel">
                  Inactive in selected period
                </div>
              </div>
              <b-popover
                target="metric-no-orders-window"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  No Orders in Window ({{ (activity.no_orders_in_window && activity.no_orders_in_window.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!activity.no_orders_in_window || !activity.no_orders_in_window.retailers || activity.no_orders_in_window.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in activity.no_orders_in_window.retailers"
                      :key="`now-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>

          <!-- 8. Never Ordered -->
          <b-col
            cols="12"
            sm="6"
            lg="3"
            class="mb-2"
          >
            <div
              id="metric-never-ordered"
              class="metric-card metric-card-danger-dark"
            >
              <div class="metric-icon">
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="24"
                />
              </div>
              <div class="metric-body">
                <div class="metric-label">
                  Never Ordered
                </div>
                <div class="metric-value">
                  {{ formatNumber(activity.never_ordered && activity.never_ordered.count) }}
                </div>
                <div class="metric-sublabel">
                  Retailers who haven't ordered yet
                </div>
              </div>
              <b-popover
                target="metric-never-ordered"
                triggers="hover"
                placement="bottom"
                custom-class="metric-popover"
              >
                <template #title>
                  Never Ordered ({{ (activity.never_ordered && activity.never_ordered.count) || 0 }})
                </template>
                <div class="retailer-list">
                  <div
                    v-if="!activity.never_ordered || !activity.never_ordered.retailers || activity.never_ordered.retailers.length === 0"
                    class="p-1 text-center text-muted"
                  >
                    No records
                  </div>
                  <ul
                    v-else
                    class="list-unstyled mb-0"
                  >
                    <li
                      v-for="(item, idx) in activity.never_ordered.retailers"
                      :key="`nev-${idx}`"
                      class="retailer-list-item"
                    >
                      <div class="font-weight-bold">
                        {{ item.name || '—' }}
                      </div>
                      <div
                        v-if="item.buyer_identifier"
                        class="text-muted small"
                      >
                        {{ item.buyer_identifier }}
                      </div>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </b-col>
        </b-row>

        <div
          v-if="!activityLoading && !hasActivityData && !activityError"
          class="text-center py-4 text-muted"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p class="mt-1">
            Select a date range to view retailer status
          </p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BCardTitle,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BSpinner,
  BAlert,
  BPopover,
} from 'bootstrap-vue'
import axios from 'axios'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BCardTitle,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BAlert,
    BPopover,
    FlatPicker,
    CustomVSelect,
  },
  data() {
    return {
      // Section 1: Report (metrics 1-6)
      // API: /marg/retailer-orders-report
      reportLoading: false,
      reportError: '',
      reportDateRange: '',
      report: {},

      // Section 2: Activity (metrics 7-8)
      // API: /marg/retailers/order-activity
      activityLoading: false,
      activityError: '',
      activityDateRange: '',
      activeUser: null,
      activeUserOptions: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      activity: {},
    }
  },
  computed: {
    hasReportData() {
      return this.report && Object.keys(this.report).length > 0
    },
    hasActivityData() {
      return this.activity && Object.keys(this.activity).length > 0
    },
  },
  watch: {
    reportDateRange(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const [start, end] = newVal.split(' to ')
        if (start && end) {
          this.fetchReport()
        }
      }
    },
    activityDateRange(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        const [start, end] = newVal.split(' to ')
        if (start && end) {
          this.fetchActivity()
        }
      }
    },
  },
  mounted() {
    const defaultRange = this.getDefaultDateRange()
    this.reportDateRange = defaultRange
    this.activityDateRange = defaultRange
    this.$nextTick(() => {
      this.fetchReport()
      this.fetchActivity()
    })
  },
  methods: {
    getDefaultDateRange() {
      const today = new Date()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(today.getDate() - 7)
      const pad = n => String(n).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      return `${fmt(sevenDaysAgo)} to ${fmt(today)}`
    },

    parseDateRange(range) {
      if (!range) return { start: '', end: '' }
      const parts = range.split(' to ')
      const start = parts[0] || ''
      const end = parts[1] || parts[0] || ''
      return { start, end }
    },

    async fetchReport() {
      const { start, end } = this.parseDateRange(this.reportDateRange)
      if (!start || !end) {
        this.reportError = 'Please select a valid date range.'
        return
      }
      this.reportLoading = true
      this.reportError = ''
      try {
        const { data } = await axios.get('/marg/retailer-orders-report', {
          params: {
            start_date: start,
            end_date: end,
          },
        })
        this.report = data || {}
      } catch (error) {
        this.reportError = error?.response?.data?.detail
          || error?.response?.data?.error
          || 'Failed to fetch order activity.'
      } finally {
        this.reportLoading = false
      }
    },

    async fetchActivity() {
      const { start, end } = this.parseDateRange(this.activityDateRange)
      if (!start || !end) {
        this.activityError = 'Please select a valid date range.'
        return
      }
      this.activityLoading = true
      this.activityError = ''
      try {
        const params = {
          start_date: start,
          end_date: end,
        }
        if (this.activeUser !== null && this.activeUser !== undefined) {
          params.active_user = this.activeUser
        }
        const { data } = await axios.get('/marg/retailers/order-activity', { params })
        this.activity = data || {}
      } catch (error) {
        this.activityError = error?.response?.data?.detail
          || error?.response?.data?.error
          || 'Failed to fetch retailer status report.'
      } finally {
        this.activityLoading = false
      }
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '0'
      return Number(value).toLocaleString('en-IN')
    },
    formatCurrency(value) {
      if (value === null || value === undefined || value === '') return '₹0'
      return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    formatPercent(value) {
      if (value === null || value === undefined) return '0%'
      const sign = value >= 0 ? '+' : ''
      return `${sign}${Number(value).toFixed(2)}%`
    },
    changeClass(value) {
      if (value === null || value === undefined) return ''
      return value >= 0 ? 'text-success' : 'text-danger'
    },
  },
}
</script>

<style lang="scss" scoped>
.metric-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid #ebe9f1;
  height: 100%;
  cursor: default;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(34, 41, 47, 0.1);
  }

  .metric-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 0.5rem;
    flex-shrink: 0;
  }

  .metric-body {
    flex: 1;
    min-width: 0;
  }

  .metric-label {
    font-size: 0.875rem;
    color: #6e6b7b;
    margin-bottom: 0.25rem;
    font-weight: 500;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #5e5873;
    line-height: 1.2;
  }

  .metric-sublabel {
    font-size: 0.75rem;
    color: #b9b9c3;
    margin-top: 0.25rem;
  }

  .metric-change {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
  }

  &.metric-card-primary .metric-icon {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }
  &.metric-card-success .metric-icon {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }
  &.metric-card-info .metric-icon {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }
  &.metric-card-warning .metric-icon {
    background: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
  }
  &.metric-card-secondary .metric-icon {
    background: rgba(130, 134, 139, 0.12);
    color: #82868b;
  }
  &.metric-card-danger .metric-icon {
    background: rgba(234, 84, 85, 0.12);
    color: #ea5455;
  }
  &.metric-card-danger-dark .metric-icon {
    background: rgba(234, 84, 85, 0.2);
    color: #c43e3f;
  }

  &[id^='metric-'] {
    cursor: pointer;
  }
}

.retailer-list-item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #ebe9f1;

  &:last-child {
    border-bottom: none;
  }
}
</style>

<style lang="scss">
.metric-popover {
  max-width: 380px;

  .popover-body {
    padding: 0;
    max-height: 360px;
    overflow-y: auto;
  }
}
</style>
